import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";

const fmtRON = (v) => `${new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2 }).format(v)} RON`;

export default function InvoicePrintPage() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("kk_token")) { setAuthed(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/orders").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) { setO(found); setTimeout(() => window.print(), 700); }
      else setError(true);
    }).catch(() => setError(true));
  }, [authed, id]);

  if (authed === false) return <Navigate to="/admin/login" replace />;
  if (authed === null) return null;
  if (error) return <div className="p-10 text-center">Comanda nu a fost gasita.</div>;
  if (!o) return <div className="p-10 text-center text-neutral-500">Se incarca...</div>;

  const subtotal = o.subtotal ?? o.total;
  const discount = o.discount || 0;
  const payLabel = o.payment_method === "ramburs" ? "Ramburs la curier" : (o.payment_status === "paid" ? "Card online (PLATIT)" : "Card online (in asteptare)");

  return (
    <div data-testid="invoice-print-page" className="tx-print bg-white text-black min-h-screen">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
        .tx-print { font-family: 'Manrope', sans-serif; }
        .tx-print .display { font-family: 'Unbounded', sans-serif; letter-spacing: -0.02em; }
      `}</style>

      <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
        <button data-testid="print-btn" onClick={() => window.print()} className="bg-[#00E5FF] text-black font-semibold px-4 py-2 text-sm uppercase tracking-wider">Printeaza</button>
        <button data-testid="close-btn" onClick={() => window.close()} className="border border-neutral-400 text-neutral-700 px-4 py-2 text-sm uppercase tracking-wider">Inchide</button>
      </div>

      <div className="max-w-3xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-start justify-between pb-6 border-b-4 border-black">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-[#00E5FF] flex items-center justify-center display font-bold text-xl">TX</div>
              <div>
                <div className="display text-2xl font-bold uppercase leading-none">TROTI<span style={{ color: "#00B8CC" }}>-X</span></div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Magazin & Service trotinete electrice</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="display text-3xl font-bold uppercase">Factura</div>
            <div className="font-mono font-bold text-lg mt-1">{o.order_number}</div>
            <div className="text-xs text-neutral-500 mt-1">Data: {new Date(o.created_at).toLocaleDateString("ro-RO")}</div>
          </div>
        </div>

        {/* Furnizor / Client */}
        <div className="grid grid-cols-2 gap-8 mt-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: "#00B8CC" }}>// Furnizor</div>
            <div className="text-sm leading-relaxed">
              <div className="font-bold">A.C.M BITY POWER S.R.L. (TROTI-X)</div>
              CUI: 51193046 · Reg. Com.: J2025005538004<br />
              Sediu: Str. Constantin Brăiloiu, Nr. 5, Parter, Ap. 1, Timișoara<br />
              Punct de lucru: Bd. Liviu Rebreanu 104A, Timișoara<br />
              IBAN: RO26BTRLRONCRT0CW7080401 — Banca Transilvania<br />
              Tel: 0743 110 277
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: "#00B8CC" }}>// Client</div>
            <div className="text-sm leading-relaxed">
              <div className="font-bold">{o.customer_name}</div>
              {o.address}, {o.city}{o.county ? `, ${o.county}` : ""}<br />
              Tel: {o.phone}
              {o.email ? <><br />Email: {o.email}</> : null}
            </div>
          </div>
        </div>

        {/* Tabel produse */}
        <table className="w-full mt-8 text-sm border-collapse">
          <thead>
            <tr className="bg-black text-white">
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">#</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Produs</th>
              <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Cant.</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Pret unitar</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {o.items.map((it, i) => (
              <tr key={i} className="border-b border-neutral-300">
                <td className="px-3 py-3 text-neutral-500">{i + 1}</td>
                <td className="px-3 py-3 font-medium">{it.name}</td>
                <td className="px-3 py-3 text-center">{it.qty}</td>
                <td className="px-3 py-3 text-right">{fmtRON(it.price)}</td>
                <td className="px-3 py-3 text-right font-bold">{fmtRON(it.price * it.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totaluri */}
        <div className="flex justify-end mt-4">
          <div className="w-72 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span className="font-medium">{fmtRON(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Reducere{o.promo_code ? ` (${o.promo_code})` : ""}</span><span className="font-medium">-{fmtRON(discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-neutral-600"><span>Transport</span><span className="font-bold text-green-700">GRATUIT</span></div>
            <div className="flex justify-between items-center border-t-2 border-black pt-3">
              <span className="text-xs uppercase tracking-widest font-bold">Total de plata</span>
              <span className="display font-bold text-2xl">{fmtRON(o.total)}</span>
            </div>
          </div>
        </div>

        {/* Plata + AWB */}
        <div className="grid grid-cols-2 gap-4 mt-8">
          <div className="border-2 border-black p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Metoda de plata</div>
            <div className="font-bold mt-1">{payLabel}</div>
          </div>
          <div className="border-2 border-black p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">AWB expediere</div>
            <div className="font-bold font-mono mt-1">{o.awb || "—"}</div>
          </div>
        </div>

        {o.note && (
          <div className="mt-6 bg-neutral-100 border-l-4 p-4 text-sm" style={{ borderColor: "#00B8CC" }}>
            <span className="font-bold">Observatii client:</span> {o.note}
          </div>
        )}

        <div className="mt-10 pt-5 border-t border-neutral-300 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Multumim pentru comanda! · TROTI-X · 0743 110 277
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            {o.order_number} · {new Date().toLocaleDateString("ro-RO")}
          </div>
        </div>
      </div>
    </div>
  );
}
